'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { ArrowRight } from 'lucide-react';
import type { PrV2MetadataSummary } from '../shared/pr-v2-metadata';
import { BranchBadge } from './pr-v2-code-badge';
import { cn } from '@/lib/utils';
type PrV2BranchRefsProps = {
  summary: Pick<PrV2SummaryResponse['pullRequest'], 'headSha' | 'baseSha'>;
  metadata: Pick<PrV2MetadataSummary, 'headRef' | 'baseRef'>;
  /** Long branch names shrink with an ellipsis instead of widening the row. */
  truncate?: boolean;
  className?: string;
};

/** The pull request's head → base branch chips, falling back to short SHAs when a ref is unknown. */
export function PrV2BranchRefs(t0) {
  const $ = _c(20);
  const {
    summary,
    metadata,
    truncate: t1,
    className
  } = t0;
  const truncate = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== metadata.headRef || $[1] !== summary) {
    t2 = metadata.headRef ?? summary.headSha.slice(0, 7);
    $[0] = metadata.headRef;
    $[1] = summary;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const headRef = t2;
  let t3;
  if ($[3] !== metadata.baseRef || $[4] !== summary) {
    t3 = metadata.baseRef ?? summary.baseSha.slice(0, 7);
    $[3] = metadata.baseRef;
    $[4] = summary;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const baseRef = t3;
  const badgeClassName = truncate ? "min-w-0 truncate" : undefined;
  const t4 = truncate && "min-w-0";
  let t5;
  if ($[6] !== className || $[7] !== t4) {
    t5 = cn("-ml-1.5 flex items-center gap-1", t4, className);
    $[6] = className;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== badgeClassName || $[10] !== headRef) {
    t6 = <BranchBadge text={headRef} className={badgeClassName} />;
    $[9] = badgeClassName;
    $[10] = headRef;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ArrowRight className="size-4 shrink-0 text-zinc-500" aria-hidden="true" />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== badgeClassName || $[14] !== baseRef) {
    t8 = <BranchBadge text={baseRef} className={badgeClassName} />;
    $[13] = badgeClassName;
    $[14] = baseRef;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t5 || $[17] !== t6 || $[18] !== t8) {
    t9 = <div className={t5}>{t6}{t7}{t8}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}